import {
  Activity,
  BarChart2,
  BookOpen,
  Car,
  Gavel,
  Globe,
  Layers,
  LineChart,
  type LucideIcon,
  Search,
  Smartphone,
  Users,
  Zap,
} from "lucide-react"
import { type KeyboardEvent, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { adminNavigationConfig } from "../libs/admin-navigation"
import { cn } from "../libs/utils"

interface Command {
  label: string
  parent?: string
  path: string
  icon: LucideIcon
}

const topLevel: Command[] = adminNavigationConfig.map((item) => ({
  label: item.label,
  path: item.path,
  icon: item.icon as LucideIcon,
}))

const domainPanels: Command[] = [
  {
    label: "Devices",
    parent: "Domain Entities",
    path: "/admin/domain?panel=devices&sub=device",
    icon: Smartphone,
  },
  {
    label: "Dispatch Groups",
    parent: "Domain Entities",
    path: "/admin/domain?panel=dispatch_groups&sub=dispatch_group",
    icon: Zap,
  },
  {
    label: "Power System",
    parent: "Domain Entities",
    path: "/admin/domain?panel=power_system&sub=overview",
    icon: Activity,
  },
  {
    label: "Pricing",
    parent: "Domain Entities",
    path: "/admin/domain?panel=pricing&sub=job_registry",
    icon: BarChart2,
  },
  {
    label: "Bidding",
    parent: "Domain Entities",
    path: "/admin/domain?panel=bidding&sub=bid",
    icon: Gavel,
  },
  {
    label: "Dispatcher",
    parent: "Domain Entities",
    path: "/admin/domain?panel=dispatcher&sub=commands",
    icon: Zap,
  },
  {
    label: "Charging Requirements",
    parent: "Domain Entities",
    path: "/admin/domain?panel=charging_requirements",
    icon: Layers,
  },
  {
    label: "Programs",
    parent: "Domain Entities",
    path: "/admin/domain?panel=programs&sub=program",
    icon: Layers,
  },
  {
    label: "Sites",
    parent: "Domain Entities",
    path: "/admin/domain?panel=sites&sub=site",
    icon: Globe,
  },
  {
    label: "Users",
    parent: "Domain Entities",
    path: "/admin/domain?panel=users&sub=tenant",
    icon: Users,
  },
  {
    label: "Vehicles",
    parent: "Domain Entities",
    path: "/admin/domain?panel=vehicles&sub=vehicle_model",
    icon: Car,
  },
  {
    label: "VSL",
    parent: "Domain Entities",
    path: "/admin/domain?panel=vsl&sub=device_registration",
    icon: Smartphone,
  },
  {
    label: "Weather",
    parent: "Domain Entities",
    path: "/admin/domain?panel=weather&sub=weather_location",
    icon: Globe,
  },
  { label: "CSMS", parent: "Domain Entities", path: "/admin/domain?panel=csms", icon: Smartphone },
]

const biddingTabs: Command[] = [
  { label: "Bidding", parent: "Bidding", path: "/admin/bidding?tab=bidding", icon: Gavel },
  {
    label: "Bidding History",
    parent: "Bidding",
    path: "/admin/bidding?tab=history",
    icon: LineChart,
  },
  {
    label: "Market Access Status",
    parent: "Bidding",
    path: "/admin/bidding?tab=market_access_stats",
    icon: Activity,
  },
  { label: "Drafts", parent: "Bidding", path: "/admin/bidding?tab=drafts", icon: BookOpen },
]

const simulatorTabs: Command[] = [
  {
    label: "Battery Simulator",
    parent: "Device Simulators",
    path: "/admin/simulator?tab=typescript",
    icon: Activity,
  },
  {
    label: "PowerPort Simulator",
    parent: "Device Simulators",
    path: "/admin/simulator?tab=powerport",
    icon: Zap,
  },
  {
    label: "CSMS Simulator",
    parent: "Device Simulators",
    path: "/admin/simulator?tab=csms",
    icon: Smartphone,
  },
]

const commands: Command[] = [...topLevel, ...domainPanels, ...biddingTabs, ...simulatorTabs]

/** Fuzzy match: every character in `query` must appear in `text` in order. */
function fuzzyMatch(text: string, query: string): boolean {
  const t = text.toLowerCase()
  const q = query.toLowerCase()
  let ti = 0
  for (const char of q) {
    ti = t.indexOf(char, ti)
    if (ti === -1) return false
    ti++
  }
  return true
}

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const filtered = query.trim()
    ? commands.filter((c) => fuzzyMatch([c.label, c.parent].filter(Boolean).join(" "), query))
    : commands

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery("")
      setActiveIndex(0)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  // Clamp activeIndex when results change
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(filtered.length - 1, 0)))
  }, [filtered.length])

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.children[activeIndex] as HTMLElement | undefined
    el?.scrollIntoView({ block: "nearest" })
  }, [activeIndex])

  const select = (cmd: Command) => {
    navigate(cmd.path)
    onClose()
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter" && filtered[activeIndex]) {
      select(filtered[activeIndex])
    } else if (e.key === "Escape") {
      onClose()
    }
  }

  if (!open) return null

  return (
    <dialog
      open
      className="fixed inset-0 z-50 flex h-full w-full items-start justify-center bg-transparent pt-[20vh] p-0 max-w-none"
      aria-label="Command palette"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl rounded-card bg-surface shadow-lift ring-1 ring-hairline overflow-hidden mx-4">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-hairline px-4">
          <Search className="size-5 shrink-0 text-content-3" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIndex(0)
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search..."
            className="command-palette-input h-12 flex-1 bg-transparent text-sm text-content placeholder:text-content-3 focus:outline-none"
          />
          <kbd className="hidden rounded border border-hairline-strong px-1.5 py-0.5 text-[10px] font-medium text-content-3 sm:inline-flex">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} aria-label="Commands" className="max-h-72 overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-content-3">No results found.</p>
          ) : (
            filtered.map((cmd, i) => {
              const Icon = cmd.icon
              const isActive = i === activeIndex
              return (
                <button
                  key={cmd.path}
                  type="button"
                  aria-selected={isActive}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => select(cmd)}
                  className={cn(
                    "btn-unstyled flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left text-sm",
                    isActive ? "bg-accent text-on-accent" : "text-content-2 hover:bg-surface-2"
                  )}
                >
                  <Icon
                    className={cn(
                      "size-4 shrink-0",
                      isActive ? "text-on-accent" : "text-content-3"
                    )}
                    aria-hidden
                  />
                  <span className="flex items-center gap-1.5 min-w-0">
                    {cmd.parent && (
                      <span
                        className={cn(
                          "shrink-0 text-xs",
                          isActive ? "text-on-accent/70" : "text-content-3"
                        )}
                      >
                        {cmd.parent} /
                      </span>
                    )}
                    <span className="truncate">{cmd.label}</span>
                  </span>
                </button>
              )
            })
          )}
        </div>
      </div>
    </dialog>
  )
}
